import { getAnalyticsConsent } from "./analytics-consent-storage";
import { getBrowserCookie } from "./cookies";

export type MetaEventParams = Record<string, string | number | boolean | string[] | undefined>;

const PENDING_PURCHASE_KEY = "pethernity_pending_meta_purchase";
const SENT_PURCHASE_KEY = "pethernity_meta_purchase_sent";

export type PendingPurchaseEvent = {
  eventId: string;
  params: MetaEventParams;
};

export function generateMetaEventId(prefix = "evt"): string {
  const id =
    typeof crypto !== "undefined" && "randomUUID" in crypto
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  return `${prefix}_${id}`;
}

export function getMetaCookies() {
  if (getAnalyticsConsent() !== "granted") {
    return {};
  }

  return {
    fbp: getBrowserCookie("_fbp") ?? undefined,
    fbc: getBrowserCookie("_fbc") ?? undefined,
  };
}

export function trackMetaEvent(
  eventName: string,
  params: MetaEventParams = {},
  eventId?: string,
): boolean {
  if (getAnalyticsConsent() !== "granted" || !window.fbq) {
    return false;
  }

  const fbq = window.fbq;
  const cleanParams = Object.fromEntries(
    Object.entries(params).filter(([, value]) => value !== undefined),
  );
  if (eventId) {
    fbq("track", eventName, cleanParams, { eventID: eventId });
  } else {
    fbq("track", eventName, cleanParams);
  }
  return true;
}

export function queuePendingPurchase(event: PendingPurchaseEvent): void {
  try {
    sessionStorage.setItem(PENDING_PURCHASE_KEY, JSON.stringify(event));
  } catch {
    /* private mode / quota */
  }
}

function clearPendingPurchase(): void {
  try {
    sessionStorage.removeItem(PENDING_PURCHASE_KEY);
  } catch {
    /* private mode */
  }
}

function isPurchaseAlreadySent(eventId: string): boolean {
  try {
    return sessionStorage.getItem(SENT_PURCHASE_KEY) === eventId;
  } catch {
    return false;
  }
}

function markPurchaseSent(eventId: string): void {
  try {
    sessionStorage.setItem(SENT_PURCHASE_KEY, eventId);
  } catch {
    /* private mode */
  }
}

export function trackPurchaseOnce(
  eventId: string,
  params: MetaEventParams = {},
): boolean {
  if (!eventId || isPurchaseAlreadySent(eventId)) {
    clearPendingPurchase();
    return false;
  }

  const sent = trackMetaEvent("Purchase", params, eventId);
  if (sent) {
    markPurchaseSent(eventId);
    clearPendingPurchase();
    return true;
  }

  queuePendingPurchase({ eventId, params });
  return false;
}

export function flushPendingMetaEvents(): void {
  if (getAnalyticsConsent() !== "granted" || !window.fbq) {
    return;
  }

  try {
    const raw = sessionStorage.getItem(PENDING_PURCHASE_KEY);
    if (!raw) return;
    const pending = JSON.parse(raw) as PendingPurchaseEvent;
    if (pending?.eventId) {
      trackPurchaseOnce(pending.eventId, pending.params ?? {});
    }
  } catch {
    /* ignore malformed storage */
  }
}
